import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Tooltip, Button,
  TextField, MenuItem, Select, FormControl, InputLabel, InputAdornment,
  Collapse, IconButton
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../apiConfig';

const LOGS_API = API_BASE_URL.replace('/espacios', '/admin/logs');

// ─── Human-readable descriptions ───────────────────────────────────────────

function getHumanDescription(log) {
  const d = log.details || {};
  const who = d._user_email || log.user_id || '?';

  switch (log.action) {
    // Espacios
    case 'CREATE':
      if (log.target_type === 'espacio')
        return `${who} creó el espacio ${d.codigo || log.target_id} (${d.tipo_espacio || ''}, ${d.direccion || ''})`;
      if (log.target_type === 'asignacion')
        return `${who} creó contrato #${log.target_id}: ${d.cliente || ''} → ${d.codigo_espacio || ''} (${d.fecha_inicio || ''} a ${d.fecha_fin || ''})`;
      if (log.target_type === 'work_order')
        return `${who} creó orden de trabajo #${log.target_id} para espacio ${d.codigo_espacio || ''}`;
      if (log.target_type === 'shared_link')
        return `${who} generó enlace compartido "${d.nombre || log.target_id}" (exp: ${d.expiracion || 'sin fecha'})`;
      if (log.target_type === 'user')
        return `${who} creó un nuevo usuario: ${d.email || d.uid || log.target_id} (${d.role || 'user'})`;
      break;

    case 'UPDATE':
      if (log.target_type === 'espacio') {
        const cambios = d.campos_actualizados || {};
        const partes = [];
        for (const [k, v] of Object.entries(cambios)) {
          partes.push(`${k} → ${v}`);
        }
        return `${who} actualizó valla ${log.target_id}${partes.length ? ': ' + partes.join(', ') : ''}`;
      }
      if (log.target_type === 'asignacion')
        return `${who} actualizó contrato #${log.target_id}`;
      if (log.target_type === 'user') {
        const cambios = d.cambios || {};
        const partes = [];
        if (cambios.role) partes.push(`rol → ${cambios.role}`);
        if (cambios.approved !== undefined) partes.push(`aprobación → ${cambios.approved ? 'sí' : 'no'}`);
        if (cambios.can_view_dashboard !== undefined) partes.push(`dashboard → ${cambios.can_view_dashboard ? 'sí' : 'no'}`);
        return `${who} modificó usuario ${d.target_user_email || d.uid_afectado || log.target_id}${partes.length ? ': ' + partes.join(', ') : ''}`;
      }
      if (log.target_type === 'work_order')
        return `${who} actualizó orden de trabajo #${log.target_id}`;
      break;

    case 'DELETE':
      if (log.target_type === 'espacio')
        return `${who} eliminó la valla ${log.target_id} (${d.direccion || 'sin dirección'})`;
      if (log.target_type === 'asignacion')
        return `${who} eliminó contrato #${log.target_id} (${d.cliente || ''}, espacio ${d.codigo_espacio || ''})`;
      if (log.target_type === 'shared_link')
        return `${who} ${d.permanente ? 'eliminó permanentemente' : 'desactivó'} el enlace "${d.nombre || log.target_id}"`;
      if (log.target_type === 'user')
        return `${who} eliminó permanentemente al usuario ${d.email || d.uid || log.target_id}`;
      break;

    case 'VIEW':
      if (log.target_type === 'shared_link')
        return `Visitante (IP: ${d.ip_visitante || '?'}) abrió enlace compartido "${d.nombre_enlace || log.target_id}"`;
      break;

    case 'INBOUND_LEAD':
      return `Lead entrante desde portal público: ${d.cliente || '?'} (${d.email || ''}) — ${(d.vallas_interes || []).length} valla(s) de interés`;

    case 'ASSIGN':
      return `${who} asignó el lead "${d.cliente || log.target_id}" a usuario ${d.asignado_a_email || d.asignado_a_uid || '?'}`;

    case 'UPDATE_STATUS':
      return `${who} cambió estado del lead #${log.target_id} → ${d.nuevo_estado || '?'}`;

    case 'EXPORT_PDF':
      return `${who} exportó PDF de informe: ${d.cantidad_espacios ?? '?'} espacios, título "${d.titulo || ''}"`;

    case 'EXPORT_PROPOSAL':
      return `${who} generó propuesta para ${d.cliente || '?'}: ${d.cantidad_espacios ?? '?'} espacio(s)`;

    default:
      break;
  }

  // Fallback: show raw  
  return log.details ? JSON.stringify(log.details) : '-';
}

// ─── Action chip colour ─────────────────────────────────────────────────────

function getActionColor(action) {
  const map = {
    CREATE: 'success',
    UPDATE: 'info',
    DELETE: 'error',
    VIEW: 'default',
    INBOUND_LEAD: 'secondary',
    ASSIGN: 'warning',
    UPDATE_STATUS: 'info',
    EXPORT_PDF: 'primary',
    EXPORT_PROPOSAL: 'primary',
  };
  return map[action] || 'default';
}

// ─── Safe date parser ────────────────────────────────────────────────────────

function formatDate(raw) {
  if (!raw) return 'Sin fecha';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw; // return raw as fallback rather than "Invalid Date"
  return d.toLocaleString('es-DO', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function LogsManager() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [search, setSearch] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const { currentUser } = useAuth();
  
  const LIMIT = 50;

  const fetchLogs = async (isLoadMore = false) => {
    if (isLoadMore) setLoadingMore(true);
    else setLoading(true);

    try {
      const nextPage = isLoadMore ? page + 1 : 1;
      const { data } = await axios.get(`${LOGS_API}?page=${nextPage}&limit=${LIMIT}`);
      
      if (isLoadMore) {
        setLogs(prev => [...prev, ...data]);
        setPage(nextPage);
      } else {
        setLogs(data);
        setPage(1);
      }

      if (data.length < LIMIT) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => { fetchLogs(); }, []);

  // Unique action types for the filter dropdown
  const actionTypes = useMemo(() => [...new Set(logs.map(l => l.action))].sort(), [logs]);

  // Apply filters
  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      const desc = getHumanDescription(log).toLowerCase();
      const matchSearch = !search || desc.includes(search.toLowerCase())
        || (log.user_id || '').toLowerCase().includes(search.toLowerCase())
        || ((log.details?._user_email || '').toLowerCase().includes(search.toLowerCase()));
      const matchAction = !filterAction || log.action === filterAction;
      return matchSearch && matchAction;
    });
  }, [logs, search, filterAction]);

  return (
    <Box sx={{ p: 4, height: '100%', overflowY: 'auto', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800} color="primary" sx={{ letterSpacing: '-0.03em' }}>
            Registro de Actividad
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Auditoría detallada de eventos del sistema ({logs.length} cargados)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => fetchLogs(false)} disabled={loading}>
            Actualizar
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Buscar por usuario, descripción..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
          }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Tipo de acción</InputLabel>
          <Select
            label="Tipo de acción"
            value={filterAction}
            onChange={e => setFilterAction(e.target.value)}
          >
            <MenuItem value=""><em>Todos</em></MenuItem>
            {actionTypes.map(a => (
              <MenuItem key={a} value={a}>
                <Chip label={a} size="small" color={getActionColor(a)} sx={{ fontWeight: 700 }} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
      ) : (
        <Paper 
          elevation={0}
          sx={{ 
            borderRadius: 4, 
            overflow: 'hidden', 
            border: '1px solid', 
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: '0 10px 30px rgba(0,0,0,0.04)'
          }}
        >
          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {[
                    { label: 'Fecha', width: 150 },
                    { label: 'Usuario', width: 180 },
                    { label: 'Acción', width: 130 },
                    { label: 'Descripción', width: 'auto' },
                    { label: '', width: 48 },
                  ].map((col) => (
                    <TableCell 
                      key={col.label}
                      sx={{ 
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        fontWeight: 700,
                        py: 2,
                        fontSize: '0.85rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        width: col.width
                      }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
            <TableBody>
              {filteredLogs.map(log => (
                <React.Fragment key={log.id}>
                  <TableRow hover sx={{ cursor: 'pointer' }}>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{formatDate(log.created_at)}</TableCell>
                    <TableCell>
                      <Tooltip title={log.user_id || ''} arrow>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
                          {log.details?._user_email || log.user_id || '—'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip label={log.action} size="small" color={getActionColor(log.action)} sx={{ fontWeight: 700, fontSize: '0.7rem' }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                        {getHumanDescription(log)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {log.details && (
                        <IconButton size="small" onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}>
                          {expandedRow === log.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                  {log.details && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ p: 0, borderBottom: expandedRow === log.id ? undefined : 'none' }}>
                        <Collapse in={expandedRow === log.id} timeout="auto" unmountOnExit>
                          <Box sx={{ bgcolor: '#f8f9fa', px: 3, py: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
                              Datos técnicos del evento:
                            </Typography>
                            <pre style={{ margin: 0, fontSize: '11px', whiteSpace: 'pre-wrap', wordBreak: 'break-all', color: '#333' }}>
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
              {filteredLogs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No hay registros que coincidan con los filtros.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {hasMore && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', borderTop: '1px solid', borderColor: 'divider' }}>
            <Button 
              variant="text" 
              onClick={() => fetchLogs(true)} 
              disabled={loadingMore}
              sx={{ fontWeight: 700 }}
              startIcon={loadingMore ? <CircularProgress size={16} /> : null}
            >
              {loadingMore ? 'Cargando más...' : 'Cargar más registros'}
            </Button>
          </Box>
        )}
      </Paper>
      )}
    </Box>
  );
}
